// WebSocket Notification Service
// Real-time notifications for deposits, withdrawals, and position updates

class NotificationWebSocket {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.userId = null;
    this.isConnected = false;
  }

  // Connect to WebSocket server
  connect(userId, token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.userId = userId;
    
    // Build WebSocket URL from backend URL
    let wsUrl;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    if (backendUrl) {
      // Convert http(s) to ws(s)
      wsUrl = backendUrl
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
      wsUrl = `${wsUrl}/api/ws/notifications/${userId}`;
    } else {
      // Fallback to current host
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/ws/notifications/${userId}`;
    }

    console.log('Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Notification WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Authenticate
        this.send({ type: 'auth', token });
        
        this.emit('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('Notification WebSocket closed:', event.code);
        this.isConnected = false;
        this.emit('disconnected');
        
        // Attempt reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.reconnectAttempts++;
            this.connect(this.userId, token);
          }, this.reconnectDelay * (this.reconnectAttempts + 1));
        }
      };

      this.ws.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        this.emit('error', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  // Send message through WebSocket
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  // Handle incoming messages
  handleMessage(data) {
    const { type, payload } = data;

    switch (type) {
      case 'notification':
        this.emit('notification', payload);
        break;
      
      case 'deposit_approved':
        this.emit('deposit', { ...payload, status: 'approved' });
        break;
      
      case 'deposit_rejected':
        this.emit('deposit', { ...payload, status: 'rejected' });
        break;
      
      case 'withdrawal_approved':
        this.emit('withdrawal', { ...payload, status: 'approved' });
        break;
      
      case 'withdrawal_rejected':
        this.emit('withdrawal', { ...payload, status: 'rejected' });
        break;
      
      case 'position_closed':
        this.emit('position_closed', payload);
        break;
      
      case 'take_profit_hit':
        this.emit('take_profit', payload);
        break;
      
      case 'stop_loss_hit':
        this.emit('stop_loss', payload);
        break;
      
      case 'liquidation':
        this.emit('liquidation', payload);
        break;
      
      case 'price_alert':
        this.emit('price_alert', payload);
        break;
      
      case 'balance_update':
        this.emit('balance_update', payload);
        break;
      
      case 'announcement':
        this.emit('announcement', payload);
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      default:
        console.log('Unknown notification type:', type);
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event, callback) {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event, data) {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  // Start heartbeat to keep connection alive
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
  }
}

// Singleton instance
const notificationWebSocket = new NotificationWebSocket();

export default notificationWebSocket;
